import { Component, useCallback, useEffect, useRef, useState } from "react";
import type { ReactNode } from "react";
import clsx from "clsx";
import type {
  PortalBorrower,
  PortalDoc,
  PortalDocStatus,
  PortalState,
} from "../../types/portal";
import { ApiError, approveDocPortal, fetchPortalForDeal } from "../../api";
import { timeAgo } from "../../lib/relative-time";
import { TabPanel } from "./TabPanel";
import { DocPortalRevokeModal } from "./DocPortalRevokeModal";
import { DocPortalReviseModal } from "./DocPortalReviseModal";
import { DocPortalSendModal } from "./DocPortalSendModal";

/**
 * Per-doc optimistic action overlay. Sits on top of server state so the user
 * sees instant pill flips on click. Cleared when a subsequent server fetch
 * brings the real state in line with the intent. Survives errors so retry
 * can reuse the same idempotency key (protects against double-action when a
 * call lands but its response was lost).
 */
interface DocActionAttempt {
  intent: "approve" | "revise";
  idempotencyKey: string;
  submitting: boolean;
  error: string | null;
}

/**
 * Render guard: Portal's deployed API does not yet match the v1.1 contract
 * (no portal_url, no progress, docs[] vs documents[], etc.). Any access that
 * relies on v1.1 fields throws during render. The boundary catches it and
 * swaps in PendingV11Pane so the page doesn't blank. Retry remounts via key.
 */
class DocPortalErrorBoundary extends Component<
  { children: ReactNode; fallback: ReactNode },
  { hasError: boolean }
> {
  state = { hasError: false };
  static getDerivedStateFromError() {
    return { hasError: true };
  }
  componentDidCatch(err: Error) {
    console.error(
      "[DocPortalTab] render error — likely Portal API schema mismatch:",
      err,
    );
  }
  render() {
    return this.state.hasError ? this.props.fallback : this.props.children;
  }
}

const POLL_MS = 10_000;

const STATUS_LABEL: Record<PortalDocStatus, string> = {
  needed: "NEEDED",
  uploaded: "UPLOADED",
  approved: "APPROVED",
  revision_requested: "REVISION",
};

interface DocPortalTabProps {
  dealId: string;
}

export function DocPortalTab({ dealId }: DocPortalTabProps) {
  // null = not loaded yet; { kind: "none" } = portal doesn't exist; { kind: "have" } = loaded
  const [state, setState] = useState<PortalState | null>(null);
  const [empty, setEmpty] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [copied, setCopied] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  const [lastAttempt, setLastAttempt] = useState<Date | null>(null);
  // Bumped on retry — used as the boundary's key so a thrown render gets a
  // fresh boundary instance (resets hasError) and the children try again.
  const [boundaryKey, setBoundaryKey] = useState(0);
  // Single-slot dialog state — mutually exclusive modals.
  const [dialog, setDialog] = useState<
    | { kind: "revoke" }
    | { kind: "revise"; docKey: string; docDisplayName: string }
    | {
        kind: "send";
        mode: "create" | "add";
        /** Required for add mode: Portal's /send requires `borrower` even on
         *  additional-doc requests. We pass the active portal's borrower
         *  through without re-prompting. */
        existingBorrower?: PortalState["borrower"];
      }
    | null
  >(null);
  // Per-doc optimistic-action overlay keyed by doc_key. See DocActionAttempt.
  const [docActions, setDocActions] = useState<Map<string, DocActionAttempt>>(
    () => new Map(),
  );
  const initialLoadDone = useRef(false);

  const load = useCallback(
    async (mode: "initial" | "manual" | "poll") => {
      if (mode === "manual") setRefreshing(true);
      try {
        const next = await fetchPortalForDeal(dealId);
        setState(next);
        setEmpty(next === null);
        setError(null);
      } catch (err) {
        if (mode === "poll") {
          setToast("⚠ Refresh failed — will retry");
        } else {
          setError(
            err instanceof ApiError
              ? `${err.code} (HTTP ${err.status})`
              : err instanceof Error
                ? err.message
                : "fetch_failed",
          );
        }
      } finally {
        if (mode === "manual") setRefreshing(false);
        if (mode === "initial") initialLoadDone.current = true;
        // Track every completion so the pending pane can show last-attempted.
        setLastAttempt(new Date());
      }
    },
    [dealId],
  );

  // Initial load + restart when dealId changes.
  useEffect(() => {
    initialLoadDone.current = false;
    setState(null);
    setEmpty(false);
    setError(null);
    void load("initial");
  }, [dealId, load]);

  // Poll every 10s while the tab is mounted.
  useEffect(() => {
    const id = window.setInterval(() => {
      void load("poll");
    }, POLL_MS);
    return () => window.clearInterval(id);
  }, [load]);

  // Toast auto-dismiss.
  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 2200);
    return () => clearTimeout(t);
  }, [toast]);

  // When fresh portal state arrives, drop any optimistic entry whose server
  // doc status now matches the intent. Keeps the Map small and prevents
  // optimistic overlays from lingering past the eventual-consistency point.
  // Errored entries (submitting:false + error set) stay until user retries
  // or the matching server state arrives, whichever first.
  useEffect(() => {
    if (!state) return;
    setDocActions((prev) => {
      if (prev.size === 0) return prev;
      let mutated = false;
      const next = new Map(prev);
      for (const [docKey, attempt] of prev) {
        if (attempt.submitting) continue;
        const serverDoc = state.docs.find((d) => d.doc_key === docKey);
        if (!serverDoc) continue;
        const matches =
          (attempt.intent === "approve" && serverDoc.status === "approved") ||
          (attempt.intent === "revise" &&
            serverDoc.status === "revision_requested");
        if (matches) {
          next.delete(docKey);
          mutated = true;
        }
      }
      return mutated ? next : prev;
    });
  }, [state]);

  /**
   * Approve handler. Generates (or reuses on retry) an idempotency key,
   * sets optimistic state, fires the API call. On success: leaves the
   * optimistic entry until server state matches. On error: leaves the entry
   * with submitting:false + error set so the user sees the error message
   * and a retry click reuses the same key.
   */
  const onApproveDoc = async (docKey: string) => {
    const existing = docActions.get(docKey);
    const key = existing?.idempotencyKey ?? crypto.randomUUID();
    setDocActions((prev) => {
      const next = new Map(prev);
      next.set(docKey, {
        intent: "approve",
        idempotencyKey: key,
        submitting: true,
        error: null,
      });
      return next;
    });
    try {
      const result = await approveDocPortal(dealId, docKey, key);
      // Already-approved → silent no-op; just clear the optimistic entry so
      // the doc renders from server state.
      if (result.no_op) {
        setDocActions((prev) => {
          const next = new Map(prev);
          next.delete(docKey);
          return next;
        });
        return;
      }
      // Keep optimistic entry as a non-submitting overlay until the next poll
      // brings server state into agreement (effect above clears it then).
      setDocActions((prev) => {
        const next = new Map(prev);
        const entry = next.get(docKey);
        if (entry) {
          next.set(docKey, { ...entry, submitting: false, error: null });
        }
        return next;
      });
    } catch (err) {
      const msg =
        err instanceof ApiError
          ? `${err.code} (HTTP ${err.status})`
          : err instanceof Error
            ? err.message
            : "approve_failed";
      setDocActions((prev) => {
        const next = new Map(prev);
        const entry = next.get(docKey);
        if (entry) {
          next.set(docKey, { ...entry, submitting: false, error: msg });
        }
        return next;
      });
      setToast(`⚠ Approve failed: ${msg}`);
    }
  };

  /**
   * Open the revise modal for a doc. The modal owns its own idempotency key
   * (and its own retry-loop on submit failure). On success, the parent
   * registers an optimistic-only attempt (no submitting/no retry path) so
   * the pill flips to REVISION_REQUESTED until server state matches.
   */
  const onReviseDoc = (docKey: string, docDisplayName: string) => {
    setDialog({ kind: "revise", docKey, docDisplayName });
  };

  const onCopyLink = async () => {
    if (!state) return;
    try {
      await navigator.clipboard.writeText(state.portal_url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    } catch {
      setToast("⚠ Copy failed");
    }
  };

  // First-paint skeleton
  if (!initialLoadDone.current && !error) {
    return (
      <TabPanel>
        <div className="doc-portal-loading">
          <span className="chat-thinking-dot" />
          <span className="chat-thinking-dot" />
          <span className="chat-thinking-dot" />
          <span className="chat-thinking-label">LOADING DOC PORTAL</span>
        </div>
      </TabPanel>
    );
  }

  if (error) {
    return (
      <TabPanel>
        <div className="doc-portal-error">
          ⚠ Failed to load doc portal: {error}
          <button
            className="doc-portal-retry"
            onClick={() => {
              setError(null);
              void load("initial");
            }}
          >
            RETRY
          </button>
        </div>
      </TabPanel>
    );
  }

  if (empty || !state) {
    return (
      <TabPanel>
        <EmptyState onSend={() => setDialog({ kind: "send", mode: "create" })} />
        {toast && <div className="edge-toast">{toast}</div>}
        {dialog?.kind === "send" && (
          <DocPortalSendModal
            dealId={dealId}
            mode={dialog.mode}
            onConfirmed={({ action, docsAdded, borrowerEmail }) => {
              setDialog(null);
              setToast(
                action === "created"
                  ? `✓ Portal sent${borrowerEmail ? ` to ${borrowerEmail}` : ""}`
                  : `✓ ${docsAdded} document${docsAdded === 1 ? "" : "s"} added`,
              );
              void load("manual");
            }}
            onCancel={() => setDialog(null)}
          />
        )}
      </TabPanel>
    );
  }

  const onRetry = () => {
    setBoundaryKey((k) => k + 1);
    void load("manual");
  };

  return (
    <DocPortalErrorBoundary
      key={`${dealId}:${boundaryKey}`}
      fallback={
        <TabPanel>
          <PendingV11Pane lastAttempt={lastAttempt} onRetry={onRetry} />
        </TabPanel>
      }
    >
      <TabPanel>
        <PortalBody
          state={state}
          refreshing={refreshing}
          copied={copied}
          docActions={docActions}
          onCopyLink={onCopyLink}
          onRefresh={() => void load("manual")}
          onRevoke={() => setDialog({ kind: "revoke" })}
          onAddDocument={() =>
            setDialog({
              kind: "send",
              mode: "add",
              existingBorrower: state.borrower,
            })
          }
          onSendNew={() => setDialog({ kind: "send", mode: "create" })}
          onApproveDoc={onApproveDoc}
          onReviseDoc={onReviseDoc}
        />
        {toast && <div className="edge-toast">{toast}</div>}
        {dialog?.kind === "revoke" && (
          <DocPortalRevokeModal
            dealId={dealId}
            borrowerName={borrowerDisplayName(state.borrower)}
            onConfirmed={() => {
              setDialog(null);
              setToast("✓ Portal revoked");
              void load("manual");
            }}
            onCancel={() => setDialog(null)}
          />
        )}
        {dialog?.kind === "revise" && (
          <DocPortalReviseModal
            dealId={dealId}
            docKey={dialog.docKey}
            docDisplayName={dialog.docDisplayName}
            onConfirmed={() => {
              // Register optimistic overlay so the pill flips immediately.
              // No idempotency key needed here — the modal already submitted
              // and won't fire again; this entry exists only to drive the UI
              // overlay until server state catches up.
              setDocActions((prev) => {
                const next = new Map(prev);
                next.set(dialog.docKey, {
                  intent: "revise",
                  idempotencyKey: crypto.randomUUID(),
                  submitting: false,
                  error: null,
                });
                return next;
              });
              setDialog(null);
              setToast("✓ Revision request sent");
              void load("manual");
            }}
            onCancel={() => setDialog(null)}
          />
        )}
        {dialog?.kind === "send" && (
          <DocPortalSendModal
            dealId={dealId}
            mode={dialog.mode}
            existingBorrower={dialog.existingBorrower}
            onConfirmed={({ action, docsAdded, borrowerEmail }) => {
              setDialog(null);
              setToast(
                action === "created"
                  ? `✓ Portal sent${borrowerEmail ? ` to ${borrowerEmail}` : ""}`
                  : `✓ ${docsAdded} document${docsAdded === 1 ? "" : "s"} added`,
              );
              void load("manual");
            }}
            onCancel={() => setDialog(null)}
          />
        )}
      </TabPanel>
    </DocPortalErrorBoundary>
  );
}

interface PortalBodyProps {
  state: PortalState;
  refreshing: boolean;
  copied: boolean;
  docActions: Map<string, DocActionAttempt>;
  onCopyLink: () => void;
  onRefresh: () => void;
  onRevoke: () => void;
  onAddDocument: () => void;
  onSendNew: () => void;
  onApproveDoc: (docKey: string) => void;
  onReviseDoc: (docKey: string, docDisplayName: string) => void;
}

function PortalBody({
  state,
  refreshing,
  copied,
  docActions,
  onCopyLink,
  onRefresh,
  onRevoke,
  onAddDocument,
  onSendNew,
  onApproveDoc,
  onReviseDoc,
}: PortalBodyProps) {
  const docsByCategory = groupByCategory(state.docs);
  const sentLabel = state.sent_at ? timeAgo(state.sent_at) : "not sent";
  const statusLabel = state.status.toUpperCase();
  const { docs_total, docs_approved, pct_complete } = state.progress;

  return (
    <div className="doc-portal-tab">
      <div className="doc-portal-header">
        <div className="doc-portal-header-row">
          <div className="doc-portal-status-line">
            <span
              className={clsx(
                "doc-portal-status-pill",
                `dp-status-${state.status}`,
              )}
            >
              {statusLabel}
            </span>
            <span className="doc-portal-status-meta">
              sent {sentLabel} · {pct_complete}% complete ({docs_approved}/
              {docs_total})
            </span>
          </div>
          <div className="doc-portal-header-actions">
            {state.status === "active" ? (
              <>
                <button
                  className="doc-portal-revoke-btn"
                  onClick={onRevoke}
                  title="Revoke portal (kills the borrower's magic link)"
                >
                  REVOKE PORTAL
                </button>
                <button
                  className="doc-portal-add-btn"
                  onClick={onAddDocument}
                  title="Add more documents to this portal"
                >
                  + ADD DOCUMENT
                </button>
              </>
            ) : (
              <button
                className="doc-portal-send-new-btn"
                onClick={onSendNew}
                title="Send a fresh portal — replaces the dead one"
              >
                + SEND NEW PORTAL
              </button>
            )}
            <button
              className="doc-portal-refresh"
              onClick={onRefresh}
              disabled={refreshing}
              title="Refresh portal state"
            >
              {refreshing ? "↻ …" : "↻ REFRESH"}
            </button>
          </div>
        </div>

        <BorrowerRow label="BORROWER" borrower={state.borrower} />
        {state.coborrower && (
          <BorrowerRow label="CO-BORROWER" borrower={state.coborrower} />
        )}

        <div className="doc-portal-link-row">
          <span className="doc-portal-link-label">PORTAL LINK</span>
          <button className="doc-portal-copy-btn" onClick={onCopyLink}>
            {copied ? "✓ COPIED" : "📋 COPY TO CLIPBOARD"}
          </button>
          <a
            className="doc-portal-link-open"
            href={state.portal_url}
            target="_blank"
            rel="noopener noreferrer"
            title="Open portal in new tab"
          >
            ↗ OPEN
          </a>
        </div>
      </div>

      {state.status !== "active" && (
        <div className={clsx("doc-portal-status-banner", `dp-banner-${state.status}`)}>
          ⚠{" "}
          {state.status === "revoked"
            ? `Portal revoked${state.revoked_at ? ` ${timeAgo(state.revoked_at)}` : ""} — borrower's magic link no longer works. Click + SEND NEW PORTAL to issue a fresh one.`
            : `Portal complete${state.completed_at ? ` ${timeAgo(state.completed_at)}` : ""} — all docs approved. Click + SEND NEW PORTAL if you need to collect more.`}
        </div>
      )}

      {docsByCategory.length === 0 ? (
        <div className="doc-portal-no-docs">
          No documents have been added to this portal yet.
        </div>
      ) : (
        docsByCategory.map(([category, docs]) => (
          <DocCategoryBlock
            key={category}
            category={category}
            docs={docs}
            docActions={docActions}
            onApproveDoc={onApproveDoc}
            onReviseDoc={onReviseDoc}
          />
        ))
      )}

      {state.esign_packet && <EsignBlock packet={state.esign_packet} />}
    </div>
  );
}

function PendingV11Pane({
  lastAttempt,
  onRetry,
}: {
  lastAttempt: Date | null;
  onRetry: () => void;
}) {
  return (
    <div className="doc-portal-pending">
      <div className="doc-portal-pending-headline">
        ⚠ Portal API upgrade pending
      </div>
      <div className="doc-portal-pending-body">
        The Doc Portal tab requires Portal API v1.1, which the Portal team is
        shipping. Once their endpoint matches the contract, this tab will
        populate automatically. No action needed from Noam.
      </div>
      <div className="doc-portal-pending-meta">
        Last attempted:{" "}
        {lastAttempt
          ? lastAttempt.toLocaleTimeString(undefined, {
              hour: "2-digit",
              minute: "2-digit",
              second: "2-digit",
            })
          : "—"}
      </div>
      <button className="doc-portal-pending-retry" onClick={onRetry}>
        ↻ RETRY
      </button>
    </div>
  );
}

/**
 * Stable category ordering: preserve the order docs first appear in the API
 * response (the portal returns them in checklist order).
 */
function groupByCategory(docs: PortalDoc[]): [string, PortalDoc[]][] {
  const order: string[] = [];
  const map = new Map<string, PortalDoc[]>();
  for (const d of docs) {
    const cat = d.category || "uncategorized";
    if (!map.has(cat)) {
      map.set(cat, []);
      order.push(cat);
    }
    map.get(cat)!.push(d);
  }
  return order.map((c) => [c, map.get(c)!]);
}

interface DocCategoryBlockProps {
  category: string;
  docs: PortalDoc[];
  docActions: Map<string, DocActionAttempt>;
  onApproveDoc: (docKey: string) => void;
  onReviseDoc: (docKey: string, docDisplayName: string) => void;
}

function DocCategoryBlock({
  category,
  docs,
  docActions,
  onApproveDoc,
  onReviseDoc,
}: DocCategoryBlockProps) {
  return (
    <div className="doc-portal-category">
      <div className="doc-portal-category-header">
        {categoryDisplayName(category)}
      </div>
      {docs.map((d) => (
        <DocRow
          key={d.doc_key}
          doc={d}
          attempt={docActions.get(d.doc_key)}
          onApprove={() => onApproveDoc(d.doc_key)}
          onRevise={() => onReviseDoc(d.doc_key, d.display_name)}
        />
      ))}
    </div>
  );
}

interface DocRowProps {
  doc: PortalDoc;
  attempt?: DocActionAttempt;
  onApprove: () => void;
  onRevise: () => void;
}

function DocRow({ doc, attempt, onApprove, onRevise }: DocRowProps) {
  // Optimistic overlay rule: while a click is in-flight, show the intended
  // status. On error (submitting:false + error), revert to server truth so
  // the user doesn't see an "APPROVED" pill above a failure message.
  const effectiveStatus: PortalDocStatus = attempt?.submitting
    ? attempt.intent === "approve"
      ? "approved"
      : "revision_requested"
    : doc.status;
  const meta = docMeta(doc);
  // Action buttons appear only when the server-side status is "uploaded" —
  // that's the only state where an approve or revise makes sense. We key off
  // `doc.status` (not effectiveStatus) so the buttons stay visible during an
  // in-flight approve, letting the user cancel by waiting / retry on error.
  const showActions = doc.status === "uploaded";
  return (
    <div className={clsx("doc-portal-doc", `dp-doc-${effectiveStatus}`)}>
      <div className="doc-portal-doc-main">
        <span className="doc-portal-doc-bullet">◆</span>
        <span className="doc-portal-doc-name">{doc.display_name}</span>
        <span className={clsx("doc-portal-doc-status", `dp-status-${effectiveStatus}`)}>
          {STATUS_LABEL[effectiveStatus]}
        </span>
        {meta && <span className="doc-portal-doc-meta">{meta}</span>}
        {showActions && (
          <div className="doc-portal-doc-actions">
            <button
              className="doc-portal-doc-approve"
              onClick={onApprove}
              disabled={attempt?.submitting}
              title="Mark this document approved"
            >
              {attempt?.submitting && attempt.intent === "approve"
                ? "APPROVING…"
                : "APPROVE"}
            </button>
            <button
              className="doc-portal-doc-revise"
              onClick={onRevise}
              disabled={attempt?.submitting}
              title="Request a re-upload with a note for the borrower"
            >
              REQUEST REVISION
            </button>
          </div>
        )}
      </div>
      {attempt?.error && (
        <div className="doc-portal-doc-action-error">
          ⚠ {attempt.error} — click the button to retry (same idempotency key).
        </div>
      )}
      {doc.status === "revision_requested" && doc.revision_note && (
        <div className="doc-portal-revision-note">
          ⚠ "{doc.revision_note}"
        </div>
      )}
      {doc.files.length > 0 && (
        <div className="doc-portal-files">
          {doc.files.map((f) => (
            <a
              key={f.upload_id}
              className="doc-portal-file"
              href={f.workdrive_view_url}
              target="_blank"
              rel="noopener noreferrer"
              title="Open in WorkDrive"
            >
              <span className="doc-portal-file-icon">📎</span>
              <span className="doc-portal-file-name">{f.filename}</span>
              <span className="doc-portal-file-age">
                · {timeAgo(f.uploaded_at)}
              </span>
            </a>
          ))}
        </div>
      )}
    </div>
  );
}

function docMeta(doc: PortalDoc): string | null {
  if (doc.status === "needed") return null;
  const parts: string[] = [];
  if (doc.upload_count > 0) {
    parts.push(`${doc.upload_count} file${doc.upload_count === 1 ? "" : "s"}`);
  }
  if (doc.last_action_at) {
    parts.push(timeAgo(doc.last_action_at));
  }
  return parts.length > 0 ? parts.join(" · ") : null;
}

function categoryDisplayName(key: string): string {
  // Portal returns category as a key like "borrower_identity"; turn it into
  // a readable header without needing the checklist catalog mounted.
  return key
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase())
    .toUpperCase();
}

function EsignBlock({
  packet,
}: {
  packet: NonNullable<PortalState["esign_packet"]>;
}) {
  const statusLabel = packet.status.toUpperCase();
  // Most-significant terminal event wins. Completed > declined/recalled/expired
  // > signed > sent. Each renders with its timestamp so Noam sees the latest
  // meaningful state at a glance.
  const event =
    packet.completed_at
      ? `completed ${timeAgo(packet.completed_at)}`
      : packet.declined_at
        ? `declined ${timeAgo(packet.declined_at)}`
        : packet.recalled_at
          ? `recalled ${timeAgo(packet.recalled_at)}`
          : packet.expired_at
            ? `expired ${timeAgo(packet.expired_at)}`
            : packet.signed_at
              ? `signed ${timeAgo(packet.signed_at)}`
              : packet.sent_at
                ? `sent ${timeAgo(packet.sent_at)}`
                : null;
  return (
    <div className="doc-portal-category">
      <div className="doc-portal-category-header">E-SIGN</div>
      <div className={clsx("doc-portal-doc", `dp-esign-${packet.status}`)}>
        <div className="doc-portal-doc-main">
          <span className="doc-portal-doc-bullet">◆</span>
          <span className="doc-portal-doc-name">{packet.display_name}</span>
          <span className="doc-portal-doc-status dp-esign-status">
            {statusLabel}
          </span>
          {event && <span className="doc-portal-doc-meta">{event}</span>}
          {packet.signed_pdf_url && (
            <a
              className="doc-portal-link-open"
              href={packet.signed_pdf_url}
              target="_blank"
              rel="noopener noreferrer"
              title="Open signed PDF"
            >
              ↗ PDF
            </a>
          )}
        </div>
      </div>
    </div>
  );
}

/** Compose a single display name from v1.1.1's split first/last fields. */
function borrowerDisplayName(b: PortalBorrower): string {
  const composed = [b.first_name, b.last_name].filter(Boolean).join(" ").trim();
  return composed || b.email || "—";
}

function BorrowerRow({
  label,
  borrower,
}: {
  label: string;
  borrower: PortalBorrower;
}) {
  const name = borrowerDisplayName(borrower);
  return (
    <div className="doc-portal-borrower">
      <span className="doc-portal-borrower-label">{label}</span>
      <span className="doc-portal-borrower-name">{name}</span>
      {borrower.email && borrower.email !== name && (
        <span className="doc-portal-borrower-email">
          &lt;{borrower.email}&gt;
        </span>
      )}
    </div>
  );
}

function EmptyState({ onSend }: { onSend: () => void }) {
  return (
    <div className="doc-portal-empty">
      <div className="doc-portal-empty-headline">
        No portal has been sent for this deal yet.
      </div>
      <div className="doc-portal-empty-body">
        Once the borrower receives their portal link, this tab will show
        every document they upload, approve, or need to revise — in real time.
      </div>
      <button className="doc-portal-send-btn" onClick={onSend}>
        + SEND DOC PORTAL
      </button>
    </div>
  );
}
